import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaCamera } from "react-icons/fa";
import { useForm } from "../../hooks/useForm";
import { useItems } from "../../hooks/useItems";
import { useUnits } from "../../hooks/useUnits";
import { useAuth } from "../../hooks/useAuth";
import ImageScanModal from "../../pages/Signature/components/ImageScanModal";
import { normalizeItemSearchText } from "../../utils/itemSearchText";
import "./InventoryForm.css";
import "./MobileInventoryForm.css";

const InventoryForm = ({ onSubmit, onCancel, initialData = null }) => {
  /** True only when editing an existing inventory row (has Firestore id). Prefill-only objects omit id. */
  const isEditingExistingRow = !!(initialData && initialData.id);

  const {
    values,
    handleChange: baseHandleChange,
    resetForm,
  } = useForm({
    itemDefinitionId: initialData?.itemDefinitionId || "",
    quantity: initialData?.quantity || 1,
    serialNumber: initialData?.serialNumber || "",
    isOrganic: initialData?.isOrganic ?? null,
    currentUnitId: initialData?.currentUnitId || "",
  });

  const { items: itemsSettings } = useItems();
  const { units } = useUnits();
  const { user } = useAuth();
  const [selectedItemDefinition, setSelectedItemDefinition] = useState(null);
  const [showItemOptions, setShowItemOptions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [showScanModal, setShowScanModal] = useState(false);

  const canScan = !!process.env.REACT_APP_VISION_API_URL;

  // Check if item has serial number requirement
  const selectedItemHasSerialNumber =
    selectedItemDefinition?.isChecked || false;

  // Custom handleChange for radio buttons
  const handleChange = (e) => {
    if (e.target.name === "isOrganic") {
      baseHandleChange({
        target: {
          name: "isOrganic",
          value: e.target.value === "true",
        },
      });
    } else {
      baseHandleChange(e);
    }
  };

  // Debounced search for item options
  const debouncedSearchTerm = useMemo(() => {
    const timer = setTimeout(() => searchTerm, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filtered items based on search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return itemsSettings;
    return itemsSettings.filter((item) =>
      normalizeItemSearchText(item.itemName).includes(
        normalizeItemSearchText(searchTerm)
      )
    );
  }, [itemsSettings, searchTerm]);

  // Update selected item definition when itemDefinitionId changes
  useEffect(() => {
    if (values.itemDefinitionId) {
      const itemDef = itemsSettings.find(
        (item) => item.id === values.itemDefinitionId
      );
      setSelectedItemDefinition(itemDef);
      if (itemDef) {
        setInputValue(itemDef.itemName);
      }
    } else {
      setSelectedItemDefinition(null);
      setInputValue("");
    }
  }, [values.itemDefinitionId, itemsSettings]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate that user has selected organic/synthetic option
    if (values.isOrganic === null || values.isOrganic === undefined) {
      alert("אנא בחר סוג פריט (אורגני או קווי)");
      return;
    }

    // Validate minimum quantity: can't go below what's already assigned
    if (isEditingExistingRow && !selectedItemHasSerialNumber) {
      const assignedQty =
        (initialData.quantity || 0) -
        (initialData.availableQuantity ?? initialData.quantity ?? 0);
      if (assignedQty > 0 && Number(values.quantity) < assignedQty) {
        alert(
          `הכמות החדשה חייבת להיות לפחות ${assignedQty} (כמות מוקצית נוכחית)`
        );
        return;
      }
    }

    // Auto-assign to unit 9263 if item has serial number
    const hasSerialNumber =
      selectedItemHasSerialNumber &&
      values.serialNumber &&
      values.serialNumber.trim() !== "";
    const parentUnit = units.find((unit) => unit.unitName === "9263");
    const autoAssignedUnitId = hasSerialNumber ? parentUnit?.id : "";

    const formData = {
      itemDefinitionId: values.itemDefinitionId,
      itemName: selectedItemDefinition?.itemName || "",
      quantity: Number(values.quantity),
      serialNumber: hasSerialNumber ? values.serialNumber : "",
      isOrganic: Boolean(values.isOrganic),
      currentUnitId: autoAssignedUnitId,
      status: initialData?.status || "available", // Keep existing status when editing
      createdBy: user?.uid || "system",
    };

    // Pass validation data to parent component
    onSubmit({
      ...formData,
      validateSerialNumber: selectedItemHasSerialNumber,
      validateQuantityMerge:
        !selectedItemHasSerialNumber && !isEditingExistingRow, // Only merge for new items
      isEdit: isEditingExistingRow, // True only when updating an existing row
    });
    resetForm();
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <div className="inventory-form-overlay">
      <div className="inventory-form-container">
        <div className="inventory-form-header">
          <div className="inventory-form-title-row">
            <h2>
              {isEditingExistingRow ? "עריכת פריט במלאי" : "הוספת פריט למלאי"}
            </h2>
            {canScan && !isEditingExistingRow && (
              <button
                type="button"
                className="inventory-scan-btn"
                onClick={() => setShowScanModal(true)}
                title="סרוק תמונה / קבלה"
              >
                <FaCamera />
              </button>
            )}
          </div>
          <button className="close-btn" onClick={handleCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="inventory-form">
          <div className="form-group">
            <label htmlFor="itemDefinitionId">סוג פריט *</label>
            <div className="item-select-container">
              <input
                type="text"
                id="itemDefinitionId"
                name="itemDefinitionId"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setSearchTerm(e.target.value);
                  setShowItemOptions(true);
                }}
                onFocus={() => setShowItemOptions(true)}
                onBlur={() => setTimeout(() => setShowItemOptions(false), 200)}
                placeholder="בחר או חפש סוג פריט"
                required
                className="item-input"
              />
              {showItemOptions && (
                <div className="item-options">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className="item-option"
                      onClick={() => {
                        setInputValue(item.itemName);
                        handleChange({
                          target: {
                            name: "itemDefinitionId",
                            value: item.id,
                          },
                        });
                        setShowItemOptions(false);
                        setSearchTerm("");
                        // If item requires serial number, set quantity to 1
                        if (item.isChecked) {
                          handleChange({
                            target: {
                              name: "quantity",
                              value: 1,
                            },
                          });
                        }
                      }}
                    >
                      {item.itemName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="quantity">כמות *</label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={values.quantity}
              onChange={handleChange}
              min={
                isEditingExistingRow
                  ? Math.max(
                      1,
                      (initialData.quantity || 0) -
                        (initialData.availableQuantity ??
                          initialData.quantity ??
                          0)
                    )
                  : 1
              }
              required
              disabled={!values.itemDefinitionId || selectedItemHasSerialNumber}
            />
            {selectedItemHasSerialNumber && (
              <div className="no-serial-note">
                ℹ️ פריט עם מספר סידורי מוגבל לכמות 1
              </div>
            )}
            {initialData?.status === "assigned" &&
              !selectedItemHasSerialNumber && (
                <div className="quantity-note">
                  ⚠️ הכמות החדשה חייבת להיות לפחות {initialData?.quantity} (כמות
                  מוקצית נוכחית)
                </div>
              )}
          </div>

          <div className="form-group">
            <label htmlFor="serialNumber">
              מספר סידורי {selectedItemHasSerialNumber ? "*" : ""}
            </label>
            <input
              type="text"
              id="serialNumber"
              name="serialNumber"
              value={values.serialNumber}
              onChange={handleChange}
              placeholder={
                selectedItemHasSerialNumber
                  ? "הזן מספר סידורי (חובה)"
                  : "פריט זה לא דורש מספר סידורי"
              }
              disabled={
                !values.itemDefinitionId || !selectedItemHasSerialNumber
              }
              required={selectedItemHasSerialNumber}
            />
            {selectedItemHasSerialNumber && (
              <div className="auto-assignment-note">
                ⚠️ פריט זה דורש מספר סידורי.
              </div>
            )}
            {!selectedItemHasSerialNumber && values.itemDefinitionId && (
              <div className="no-serial-note">
                ℹ️ פריט זה לא דורש מספר סידורי
              </div>
            )}
          </div>

          <div className="form-group radio-group">
            <label className="radio-label">סוג פריט *</label>
            <div className="radio-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="isOrganic"
                  value="false"
                  checked={values.isOrganic === false}
                  onChange={handleChange}
                  disabled={!values.itemDefinitionId}
                />
                <span className="radio-custom"></span>
                קווי
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="isOrganic"
                  value="true"
                  checked={values.isOrganic === true}
                  onChange={handleChange}
                  disabled={!values.itemDefinitionId}
                />
                <span className="radio-custom"></span>
                אורגני
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-submit">
              {isEditingExistingRow ? "עדכן" : "הוסף"}
            </button>
            <button type="button" onClick={handleCancel} className="btn-cancel">
              ביטול
            </button>
          </div>
        </form>
      </div>

      {showScanModal && (
        <ImageScanModal
          isOpen={showScanModal}
          onClose={() => setShowScanModal(false)}
          onItemsConfirmed={(scannedItems) => {
            const parentUnit = units.find((u) => u.unitName === "9263");
            scannedItems.forEach((item) => {
              const itemDef = itemsSettings.find(
                (i) => i.itemName === item.itemName
              );
              onSubmit({
                itemDefinitionId: itemDef?.id || "",
                itemName: item.itemName,
                quantity: item.quantity || 1,
                serialNumber: "",
                isOrganic: item.isOrganic ?? false,
                currentUnitId: parentUnit?.id || "",
                status: "available",
                createdBy: user?.uid || "system",
                makat: item.makat || "",
                note: item.note || "",
                validateSerialNumber: false,
                validateQuantityMerge: true,
                isEdit: false,
              });
            });
            setShowScanModal(false);
          }}
        />
      )}
    </div>
  );
};

export default InventoryForm;
